import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DARK_THEME,
  DEFAULT_THEME,
  MINIMAL_THEME,
  ThemeTokens,
} from '../../../styles/config/theme-config';
import {
  CUPERTINO_DARK_THEME,
  CUPERTINO_THEME,
  DISCORD_DARK_THEME,
  DISCORD_THEME,
} from '../../../styles/config/extra-themes';
import { LocalStorageUtil } from '../../core_modules/utils/local-storage-util';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private appliedKeys: string[] = [];
  private isBrowser: boolean;

  private baseTheme: ThemeTokens = MINIMAL_THEME;
  private get baseDarkTheme(): ThemeTokens {
    switch (this.activeBaseTheme()) {
      case 'minimal':
        return {
          ...MINIMAL_THEME,
          ...DARK_THEME,
        };

      case 'default':
        return {
          ...DEFAULT_THEME,
          ...DARK_THEME,
        };

      default:
        return {
          ...this.baseTheme,
          ...DARK_THEME,
        };
    }
  }

  private activeModifier: ThemeTokens = {};
  private customOverrides: ThemeTokens = {};

  public readonly isDarkTheme = signal(false);
  public readonly activeBaseTheme = signal('minimal');

  private readonly bases: Record<string, ThemeTokens> = {
    default: DEFAULT_THEME,
    minimal: MINIMAL_THEME,
    cupertino: CUPERTINO_THEME,
    discord: DISCORD_THEME,
  };

  private readonly darkModifiers: Record<string, ThemeTokens> = {
    default: DARK_THEME,
    minimal: DARK_THEME,
    cupertino: CUPERTINO_DARK_THEME,
    discord: DISCORD_DARK_THEME,
  };

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Default application-wide launch with Discord theme active
    if (this.isBrowser) {
      this.isDarkTheme.set(LocalStorageUtil.getStorage().themeMode === 'dark');

      if (this.isDarkTheme()) {
        this.applyTheme(this.baseDarkTheme);
      } else {
        this.applyTheme(this.baseTheme);
      }

      // Load custom overrides from localStorage
      const savedCustom = localStorage.getItem('theme_custom_overrides');
      if (savedCustom) {
        try {
          this.customOverrides = JSON.parse(savedCustom);
          this.applyTheme(this.customOverrides);
        } catch {
          this.customOverrides = {};
        }
      }
    }
  }

  /**
   * Switch between built-in base themes and automatically re-apply dark modifiers if active.
   */
  setActiveBase(name: string, event?: MouseEvent): void {
    const key = name ? name.toLowerCase() : 'default';
    if (this.bases[key]) {
      this.activeBaseTheme.set(key);
      this.baseTheme = this.bases[key];
      if (this.isDarkTheme()) {
        this.activeModifier = this.darkModifiers[key] || DARK_THEME;
      } else {
        this.activeModifier = {};
      }
      this._reapply(event);
    }
  }
  /**
   * Toggle dark mode using the correct modifier for the active base theme.
   */
  toggleDarkMode(event?: MouseEvent): void {
    this.isDarkTheme.update((d) => !d);
    LocalStorageUtil.setStorage({
      ...LocalStorageUtil.getStorage(),
      themeMode: this.isDarkTheme() ? 'dark' : 'light',
    });
    this._applyDarkModifier(event);
  }

  private _applyDarkModifier(event?: MouseEvent): void {
    if (this.isDarkTheme()) {
      const modifier = this.darkModifiers[this.activeBaseTheme()] || DARK_THEME;
      this.setModifier(modifier, event);
    } else {
      this.clearModifier(event);
    }
  }

  /**
   * Set the base aesthetic theme layer (e.g. Brand variables).
   */
  setBaseTheme(tokens: ThemeTokens, event?: MouseEvent): void {
    this.baseTheme = tokens;
    this._reapply(event);
  }

  /**
   * Add a modifier theme layer (e.g. Dark Mode) on top of the base layer.
   */
  setModifier(tokens: ThemeTokens, event?: MouseEvent): void {
    this.activeModifier = tokens;
    this._reapply(event);
  }

  /**
   * Clear any active modifiers, returning exactly to the base theme.
   */
  clearModifier(event?: MouseEvent): void {
    this.activeModifier = {};
    this._reapply(event);
  }

  /**
   * Re-evaluates merging baseTheme, activeModifier, and customOverrides, and applies them.
   */
  private _reapply(event?: MouseEvent): void {
    const combined = {
      ...this.baseTheme,
      ...this.activeModifier,
      ...this.customOverrides,
    };
    if (!this.isBrowser || !(document as any).startViewTransition) {
      this._clearAppliedTokens();
      this.applyTheme(combined);
      return;
    }

    const transition = (document as any).startViewTransition(() => {
      this._clearAppliedTokens();
      this.applyTheme(combined);
    });

    transition.ready
      .then(() => {
        const root = document.documentElement;

        // Outgoing view: smooth crossfade out
        root.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 300,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-old(root)',
        });

        // Incoming view: smooth crossfade in
        root.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 300,
          fill: 'both',
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        });
      })
      .catch(() => {
        // Transition was skipped/aborted — ignore.
      });
  }

  /**
   * Apply a full or partial theme.
   * Only the provided keys will be overridden; everything else keeps
   * its default from _tokens.css.
   *
   * @param tokens — Partial<ThemeTokens> from backend JSON or a preset
   */
  applyTheme(tokens: ThemeTokens): void {
    if (!this.isBrowser) return;

    const root = document.documentElement;

    Object.entries(tokens).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        root.style.setProperty(key, value);
        if (!this.appliedKeys.includes(key)) {
          this.appliedKeys.push(key);
        }
      }
    });
  }

  /**
   * Set a single CSS custom property at runtime.
   */
  setToken(
    key:
      | keyof ThemeTokens
      | `--app-${string}`
      | `--mbank-${string}`
      | `--color-${string}`,
    value: string,
  ): void {
    if (!this.isBrowser) return;

    (this.customOverrides as any)[key] = value;
    localStorage.setItem(
      'theme_custom_overrides',
      JSON.stringify(this.customOverrides),
    );

    document.documentElement.style.setProperty(key, value);
    if (!this.appliedKeys.includes(key)) {
      this.appliedKeys.push(key);
    }
  }

  /**
   * Get the current computed value of a token.
   */
  getToken(
    key:
      | keyof ThemeTokens
      | `--app-${string}`
      | `--mbank-${string}`
      | `--color-${string}`,
  ): string {
    if (!this.isBrowser) return '';

    return getComputedStyle(document.documentElement)
      .getPropertyValue(key)
      .trim();
  }

  private _clearAppliedTokens(): void {
    if (!this.isBrowser) return;
    const root = document.documentElement;
    this.appliedKeys.forEach((key) => {
      root.style.removeProperty(key);
    });
    this.appliedKeys = [];
  }

  /**
   * Remove all runtime overrides (including custom).
   * Elements revert to the defaults defined in _tokens.css.
   */
  resetTheme(): void {
    this._clearAppliedTokens();
    this.customOverrides = {};
    if (this.isBrowser) {
      localStorage.removeItem('theme_custom_overrides');
    }
  }

  /**
   * Check if a theme (any overrides) is currently applied.
   */
  hasOverrides(): boolean {
    return this.appliedKeys.length > 0;
  }

  /**
   * Get all currently applied override keys.
   */
  getAppliedKeys(): readonly string[] {
    return [...this.appliedKeys];
  }

  // ══════════════════════════════════════════════════════════════
  // Legacy Compatibility Helpers
  // ══════════════════════════════════════════════════════════════

  /**
   * Compatibility helper for setting theme mode ('light', 'dark', 'system') or preset base theme.
   */
  setTheme(themeNameOrMode: string, event?: MouseEvent): void {
    if (!themeNameOrMode) return;
    const key = themeNameOrMode.toLowerCase();
    if (key === 'dark') {
      if (!this.isDarkTheme()) {
        this.toggleDarkMode(event);
      }
    } else if (key === 'light') {
      if (this.isDarkTheme()) {
        this.toggleDarkMode(event);
      }
    } else if (key === 'system') {
      const prefersDark =
        this.isBrowser &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (this.isDarkTheme() !== prefersDark) {
        this.toggleDarkMode(event);
      }
    } else if (this.bases[key]) {
      this.setActiveBase(key, event);
    }
  }

  /**
   * Compatibility helper to toggle dark/light theme mode.
   */
  toggleTheme(event?: MouseEvent): void {
    this.toggleDarkMode(event);
  }

  /**
   * Compatibility helper to check if dark theme is active.
   */
  isDarkMode(): boolean {
    return this.isDarkTheme();
  }

  /**
   * Compatibility helper to load user saved theme preferences.
   */
  loadTheme(): void {
    if (!this.isBrowser) return;
    const savedCustom = localStorage.getItem('theme_custom_overrides');
    if (savedCustom) {
      try {
        const parsed = JSON.parse(savedCustom);
        this.applyTheme(parsed);
      } catch {
        // Ignore
      }
    }
  }
}
